import { Activity } from '@/lib/types';
import { formatDateTime, haceCuanto } from '@/lib/utils';
import { useAutores } from '@/lib/autores';
import {
  FileText, Phone, Mail, MapPin, MessageCircle, Users, GitBranch, Paperclip, CheckSquare, Trash2,
  Pencil, UserPlus,
} from 'lucide-react';

const typeIcons: Record<string, any> = {
  note: FileText,
  call: Phone,
  email: Mail,
  visit: MapPin,
  whatsapp: MessageCircle,
  meeting: Users,
  stage_change: GitBranch,
  document: Paperclip,
  task: CheckSquare,
  lead_updated: Pencil,
  lead_captured: UserPlus,
};

const typeLabels: Record<string, string> = {
  note: 'Nota',
  call: 'Llamada',
  email: 'Email',
  visit: 'Visita',
  whatsapp: 'WhatsApp',
  meeting: 'Reunión',
  stage_change: 'Cambio de fase',
  document: 'Documento',
  task: 'Tarea',
  lead_updated: 'Edición',
  lead_captured: 'Lead captado',
};

// Por encima de esto, el antes y el después no caben en una línea y se
// pliegan: son casi siempre las notas.
const LARGO_EN_LINEA = 60;

interface Props {
  activities: Activity[];
  onDelete?: (id: string) => void;
  /** Nombre de una fase por su id, para leer los cambios de fase. */
  nombreFase?: (id: string) => string | undefined;
  /** Cómo se lee el valor guardado de un campo editado (códigos de zona, de estado…). */
  valorLegible?: (campo: string, valor: string) => string;
  /**
   * Alta de la ficha. Va al final, como primer hito, y no es una actividad
   * guardada: sale de `createdAt`, así que tampoco se puede borrar.
   */
  alta?: { titulo: string; fecha: string; por?: string };
}

export default function ActivityTimeline({ activities, onDelete, nombreFase, valorLegible, alta }: Props) {
  const autor = useAutores();
  /** «fecha · hace X · quién», sin el último tramo si no se sabe quién. */
  const cuando = (fecha: string, por?: string) => {
    const quien = autor(por);
    return `${formatDateTime(fecha)} · ${haceCuanto(fecha)}${quien ? ` · ${quien}` : ''}`;
  };

  if (activities.length === 0 && !alta) {
    return <p className="text-sm text-sereno-dark/40">Sin actividad registrada</p>;
  }

  const legible = (campo: string, valor: string) => (valor && valorLegible ? valorLegible(campo, valor) : valor);

  return (
    <div className="space-y-4">
      {activities.map((activity, index) => {
        const Icon = typeIcons[activity.activityType] || FileText;
        const etiqueta = typeLabels[activity.activityType] || activity.activityType;
        const cambios = activity.metadata?.cambios;
        const { fromStage, toStage } = activity.metadata || {};
        return (
          <div key={activity.id} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 rounded-full bg-sereno-blue flex items-center justify-center flex-shrink-0">
                <Icon size={14} className="text-sereno-dark/60" />
              </div>
              {(index < activities.length - 1 || alta) && <div className="w-px flex-1 bg-gray-200 mt-1" />}
            </div>
            <div className="flex-1 min-w-0 pb-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Las que deja el webhook de Facebook no traen título. */}
                  <span className="text-sm font-medium">{activity.title || etiqueta}</span>
                  {activity.title && activity.title !== etiqueta && (
                    <span className="badge bg-gray-100 text-gray-500 text-xs">{etiqueta}</span>
                  )}
                </div>
                {onDelete && (
                  <button
                    onClick={() => onDelete(activity.id)}
                    className="p-1 rounded hover:bg-red-50 text-sereno-dark/20 hover:text-red-400 transition-colors flex-shrink-0"
                    title="Eliminar actividad"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
              {activity.activityType === 'stage_change' && toStage && nombreFase && (
                <p className="text-sm text-sereno-dark/60 mt-1">
                  {fromStage && <>{nombreFase(fromStage) || 'Fase eliminada'} → </>}
                  <span className="font-medium text-sereno-dark/70">{nombreFase(toStage) || 'Fase eliminada'}</span>
                </p>
              )}
              {cambios && cambios.length > 0 ? (
                <ul className="mt-1 space-y-1">
                  {cambios.map(c => {
                    const antes = legible(c.campo, c.antes);
                    const despues = legible(c.campo, c.despues);
                    const largo = Math.max(antes.length, despues.length) > LARGO_EN_LINEA
                      || antes.includes('\n') || despues.includes('\n');
                    if (largo) {
                      return (
                        <li key={c.campo} className="text-sm text-sereno-dark/60">
                          <details>
                            <summary className="cursor-pointer">
                              <span className="font-medium text-sereno-dark/70">{c.etiqueta}</span>: ver antes y después
                            </summary>
                            <div className="mt-1 space-y-2 text-xs">
                              <div>
                                <p className="text-sereno-dark/35 uppercase tracking-wider">Antes</p>
                                <p className="whitespace-pre-wrap text-sereno-dark/50">{antes || '—'}</p>
                              </div>
                              <div>
                                <p className="text-sereno-dark/35 uppercase tracking-wider">Después</p>
                                <p className="whitespace-pre-wrap">{despues || '—'}</p>
                              </div>
                            </div>
                          </details>
                        </li>
                      );
                    }
                    return (
                      <li key={c.campo} className="text-sm text-sereno-dark/60 break-words">
                        <span className="font-medium text-sereno-dark/70">{c.etiqueta}:</span>{' '}
                        <span className="text-sereno-dark/35 line-through">{antes || '—'}</span>
                        {' → '}
                        {despues || '—'}
                      </li>
                    );
                  })}
                </ul>
              ) : activity.description && (
                <p className="text-sm text-sereno-dark/60 mt-1 whitespace-pre-wrap">{activity.description}</p>
              )}
              <p className="text-xs text-sereno-dark/30 mt-1" title={activity.createdAt}>
                {cuando(activity.createdAt, activity.createdBy)}
              </p>
            </div>
          </div>
        );
      })}
      {alta && (
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full bg-sereno-blue flex items-center justify-center flex-shrink-0">
            <UserPlus size={14} className="text-sereno-dark/60" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium">{alta.titulo}</span>
            <p className="text-xs text-sereno-dark/30 mt-1" title={alta.fecha}>
              {cuando(alta.fecha, alta.por)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
